import NavigableValidation from "./navigableValidation";
import FieldPresenceValidator from "~/models/navigableValidators/fieldPresenceValidator";
import {TValidator} from "~/interfaces/validationInterfaces";
import ModelMappingValue from "~/models/mapping/modelMappingValue";
import NavigableValidator, {INavigableValidatorOptions} from "~/models/navigableValidators/navigableValidator";
import {ModelMappingValuesType} from "../../../formability/mappingBuilder/enums/modelMappingValuesType";
import {ValidationModalSection} from "~/models/navigableValidations/validationSections";
import {ErrorType} from "~/enums/ErrorType";
import {ValidationErrorLevel} from "~/models/validationError";
import {isNotMultiPicklistToSinglePicklist} from "~/models/validators/picklistDataTypeValidator";
import {
  allBuiltinMappingValues,
  isObjectModelBuiltinPropertyAccess,
  ObjectModelBuiltinPropertyType
} from "~/enums/objectModelBuiltinPropertyType";
import {AccessPermission} from "~/enums/accessPermission";
import {
  ModelMappingCantAddValues,
  ModelMappingRules
} from "../../../formability/mappingBuilder/enums/model-mapping-rules";
import {DimensionalType} from "~/enums/dimensionalType";
import ModelMappingOriginObjectModel from "~/models/mapping/modelMappingOriginObjectModel";
import ModelMappingOriginForm from "~/models/mapping/modelMappingOriginForm";
import {objectModelHolderTypeToDocumentOwnerType} from "~/enums/objectModelRecordHolderType";
import ModelMappingValueOriginFormElement from "~/models/mapping/modelMappingValueOriginFormElement";
import {EnumHelper} from "~/helpers/enumHelper";
import FormElement from "../../../formability/form_builder/models/formElements/formElement";
import ObjectModelProperty from "~/models/objectModelProperties/objectModelProperty";
import ModelMappingOriginSalesforce from "~/models/mapping/modelMappingOriginSalesforce";
import {SalesforceInstanceHelper} from "~/models/mapping/salesforceHelpers/salesforceInstanceHelper";
import ObjectModelRelation from "../objectModelRelations/objectModelRelation";
import ModelMappingValidation from "~/models/navigableValidations/modelMappingValidation";
import ModelMappingDefinition from "~/models/mapping/modelMappingDefinition";

export default class ModelMappingValueValidation<T extends ModelMappingValue> extends NavigableValidation<ModelMappingValue> {
  // Note: there is a good chance your validation should not run for any of the mapping rules in ModelMappingCantAddValues.
  // You will probably want to incorporate below line at the top of your conditional in your validator:
  // if (ModelMappingCantAddValues.includes(model.modelMapping.rule)) return false
  validators = [
    this.dataTypeWarningValidator(),
    this.dataTypeErrorValidator(),
    this.overrideRecordHolderEmailWarning(),
    this.multiPicklistToSinglePicklistValidator(),
    this.readOnlyBuiltInsErrorValidator(),
    this.readOnlyLookupKeyWarning(),
    this.missingSourceReferenceValidator(),
    this.invalidRecordIdUsageValidator(),
    this.missingDestinationReferenceValidator(),
    this.fileDimensionalTypeMismatchWarningValidator(),
    this.hasCompatibleOwnerTypesValidator(),
    this.recordHolderPreserveOwnershipErrorValidator(),
    this.destinationOnLookupKeysIsIndexed(),
    ...Object.keys(this.presence_validatable_fields).map<TValidator<T>>(field => {
      return new FieldPresenceValidator({
        validatorKey: field,
        section: ValidationModalSection.Layout,
        ...this.presence_validatable_fields[field],
        conditional: (model: ModelMappingValue): boolean => !ModelMappingCantAddValues.includes(model.modelMapping.rule)
      });
    })
  ]

  get presence_validatable_fields(): { [key in keyof T]?: Partial<INavigableValidatorOptions<T>> } {
    const opts: { [key in keyof T]?: Partial<INavigableValidatorOptions<T>> } = {
      "source_value_identifier": {
        conditionalField: "type",
        conditionalValue: ModelMappingValuesType.Value,
      } ,
      "mapping_formula": {
        conditionalField: "type",
        conditionalValue: ModelMappingValuesType.Formula
      },
      "destination_value_identifier": {
        message: this.destinationValueMessage(),
        beforeReveal: async () => {
          await window.requestAnimationFrame(async () => {
            await this.scrollToAndOpenModelMapping();
          })
        },
        containerElementSelector: ".scroll-area.mapping-area"
      },
    }
    Object.keys(opts).forEach(k => opts[k].displayName = this.model.getDisplayName(k));
    return opts;
  }

  protected getAnchorHash(field: string, value?): string {
    const mappingIndex = ModelMappingDefinition.baseViewId(this.model.parent)
    const mappingValueIndex = this.model.sort_order;
    const fieldInput = (field === 'missingSourceReference') ? 'source' : 'destination'
    return `model_mapping_${mappingIndex}_value_${mappingValueIndex}_${fieldInput}`;
  };

  destinationValueMessage(): string {
    return `Source value needs a destination`
  }

  async scrollToAndOpenModelMapping(): Promise<JQuery> {
    return ModelMappingValidation.scrollToAndOpenModelMapping($(ModelMappingDefinition.documentActionsViewId(this.model.parent)))
  }

  destinationOnLookupKeysIsIndexed(): NavigableValidator<ModelMappingValue> {
    return new NavigableValidator(Object.assign({
      errorLevel: ValidationErrorLevel.Error,
      validatorKey: 'dataTypeError',
      section: ValidationModalSection.ValueMappings,
      conditional: (model: ModelMappingValue): boolean => {
        return model.isLookupKey
      },
      validator: (value, model: ModelMappingValue) => {
        return (model.destination.isIndexed || model.destination_is_default_property)||
          (model.modelMapping.rule === ModelMappingRules.FormPrepopulation ||
            EnumHelper.toArray(ObjectModelBuiltinPropertyType).map(x=> `${x}`).includes(model.destination.id))
      },
      message: "ERROR",
      type: ErrorType.lookupKeyDestinationNotIndexed,
    }, this.defaultMappingValueValidatorOptions))
  }

  dataTypeWarningValidator(): NavigableValidator<ModelMappingValue> {
    return new NavigableValidator(Object.assign({
      errorLevel: ValidationErrorLevel.Warning,
      validatorKey: 'dataTypeWarning',
      section: ValidationModalSection.ValueMappings,
      conditional: (model: ModelMappingValue): boolean => {
        if (ModelMappingCantAddValues.includes(model.modelMapping.rule)) return false
        return (
          model.type === ModelMappingValuesType.Value &&
          !!model.destination_value_identifier &&
          !!model.destination.origin &&
          !(model.source.valueType === "Input Field" && model.destination.valueType === "Number")
        );
      },
      validator: (value, model: ModelMappingValue) => {
        return model.areMappingDataTypesCompatible
      },
      message: "These data types are not recommended to map to one another",
      type: ErrorType.dataTypeMismatchWarning,
    }, this.defaultMappingValueValidatorOptions))
  }

  dataTypeErrorValidator(): NavigableValidator<ModelMappingValue> {
    return new NavigableValidator(Object.assign({
      errorLevel: ValidationErrorLevel.Error,
      validatorKey: 'dataTypeError',
      section: ValidationModalSection.ValueMappings,
      conditional: (model: ModelMappingValue): boolean => {
        if (ModelMappingCantAddValues.includes(model.modelMapping.rule)) return false
        return (
          model.type === ModelMappingValuesType.Value &&
          !!model.destination_value_identifier &&
          !!model.destination.origin &&
          (model.source.valueType === "Input Field" && model.destination.valueType === "Number")
        );
      },
      validator: (value, model: ModelMappingValue) => {
        return model.areMappingDataTypesCompatible
      },
      message: "These data types cannot to map to one another",
      type: ErrorType.dataTypeMismatchError,
    }, this.defaultMappingValueValidatorOptions))
  }

  multiPicklistToSinglePicklistValidator(): NavigableValidator<ModelMappingValue> {
    return new NavigableValidator(Object.assign({
      validatorKey: 'multiPicklistToSinglePicklist',
      section: ValidationModalSection.ValueMappings,
      conditional: (model: ModelMappingValue): boolean => {
        if (ModelMappingCantAddValues.includes(model.modelMapping.rule)) return false
        return (
          model.type === ModelMappingValuesType.Value &&
          !!model.destination_value_identifier &&
          !!model.destination?.origin
        );
      },
      validator: (value, model: ModelMappingValue) => {
        return isNotMultiPicklistToSinglePicklist(model)
      },
      message: "Multi Picklist cannot map to Single Picklist",
      type: ErrorType.multiPicklistToSingle,
    }, this.defaultMappingValueValidatorOptions))
  }

  overrideRecordHolderEmailWarning(): NavigableValidator<ModelMappingValue> {
    return new NavigableValidator(Object.assign({
      validatorKey: 'updateRecordHolderEmailWarning',
      section: ValidationModalSection.ValueMappings,
      errorLevel: ValidationErrorLevel.Warning,
      type: ErrorType.recordHolderEmailWarning,
      message: "Pushing to the Record Holder Email field will over-write the current owner and assign the record to the user with the overwritten email.",
      conditional: (model: ModelMappingValue): boolean => {
        if (ModelMappingCantAddValues.includes(model.modelMapping.rule)) return false
        return model.type === ModelMappingValuesType.Value &&
          !!model.source && !!model.destination && model.mappingIncludesBuiltin;
      },
      validator: (value, model: ModelMappingValue) => {
        const comparator = model.isPush ? model.destination_value_identifier : model.source_value_identifier;
        const enumVal = ObjectModelBuiltinPropertyType.RecordHolderEmail
        return comparator !== String(enumVal);
      },
    }, this.defaultMappingValueValidatorOptions))
  }

  readOnlyBuiltInsErrorValidator(): NavigableValidator<ModelMappingValue> {
    return new NavigableValidator(Object.assign({
      validatorKey: 'readOnlyBuiltInsError',
      section: ValidationModalSection.ValueMappings,
      errorLevel: ValidationErrorLevel.Error,
      type: ErrorType.readOnlyBuiltInsError,
      message: "Read-only fields cannot be mapped to, they must be mapped from",
      conditional: (model: ModelMappingValue): boolean => {
        if (ModelMappingCantAddValues.includes(model.modelMapping.rule)) return false
        if (model.isLookupKey) return false
        if (model.modelMapping.rule === ModelMappingRules.FormPrepopulation) return false;

        const modelMappingOrigin = model.isPush ? model.parent.destinationOrigin : model.parent.sourceOrigin;
        if (SalesforceInstanceHelper.modelMappingOriginIsSalesforce(modelMappingOrigin)) return false

        const property = model.isPush ? model.destination.origin : model.source.origin;

        return (
          model.type === ModelMappingValuesType.Value &&
          !!model.source &&
          !!model.destination &&
          !(property instanceof ObjectModelRelation)
        )
      },
      validator: (value, model: ModelMappingValue) => {
        const comparator = model.isPush ? model.destination_value_identifier : model.source_value_identifier;
        if (!comparator) return true;
        if (allBuiltinMappingValues.includes(Number(comparator))) {
          return !isObjectModelBuiltinPropertyAccess(Number(comparator), [AccessPermission.Read])
        } else {
          const id = comparator.split('__');
          const origin = model.isPush ? model.parent.destinationOrigin.origin : model.parent.sourceOrigin.origin;
          const ws = id[1] ? origin['workflow_steps'][id[1]] : null;

          if (ws && ws.isPaymentStep) {
            // as of now payment step information is read only
            return false;
          }

          const property = model.isPush ? model.destination.origin : model.source.origin;
          if (property instanceof FormElement) {
            return !property?.is_readonly
          } else if (property instanceof ObjectModelProperty) {
            return !property?.isReadOnly
          }
        }
      }
    }, this.defaultMappingValueValidatorOptions))
  }

  readOnlyLookupKeyWarning(): NavigableValidator<ModelMappingValue> {
    return new NavigableValidator(Object.assign({
      validatorKey: 'readOnlyLookupKeyWarning',
      section: ValidationModalSection.ValueMappings,
      errorLevel: ValidationErrorLevel.Warning,
      type: ErrorType.readOnlyLookupKeyWarning,
      message: "Mapping cannot push to Read-Only field",
      conditional: (model: ModelMappingValue): boolean => {
        if (!model.isLookupKey) return false
        if (ModelMappingCantAddValues.includes(model.modelMapping.rule)) return false

        const modelMappingOrigin = model.isPush ? model.parent.destinationOrigin : model.parent.sourceOrigin;
        if (modelMappingOrigin instanceof ModelMappingOriginSalesforce) return false

        return (
          model.type === ModelMappingValuesType.Value &&
          !!model.source &&
          !!model.destination
        )
      },
      validator: (value, model: ModelMappingValue) => {
        const comparator = model.isPush ? model.destination_value_identifier : model.source_value_identifier;
        if (!comparator) return true;
        if (allBuiltinMappingValues.includes(Number(comparator))) {
          return !isObjectModelBuiltinPropertyAccess(Number(comparator), [AccessPermission.Read])
        } else {
          const property = model.isPush ? model.destination.origin : model.source.origin;
          if (property instanceof FormElement) {
            return !property?.is_readonly
          } else if (property instanceof ObjectModelProperty) {
            return !property?.isReadOnly
          }
        }
      }
    }, this.defaultMappingValueValidatorOptions))
  }
  hasCompatibleOwnerTypesValidator(): NavigableValidator<ModelMappingValue> {
    return new NavigableValidator(Object.assign({
      validatorKey: 'hasCompatibleOwnerTypes',
      section: ValidationModalSection.Layout,
      conditional: (model: ModelMappingValue): boolean => {
        if (ModelMappingCantAddValues.includes(model.modelMapping.rule)) return false
        return (!!model.source && !!model.destination && ((model.mappingDestinationIsClaimRecordField || model.mappingDestinationIsSubmitterEmail) &&
          (model.mappingSourceIsClaimRecordField || model.mappingSourceIsSubmitterEmail)))
      },
      validator: (value, model: ModelMappingValue) => {
        if (model.modelMapping.sourceOrigin instanceof ModelMappingOriginObjectModel && model.modelMapping.destinationOrigin instanceof ModelMappingOriginObjectModel) {
          return model.modelMapping.sourceOrigin.model.holder_type === model.modelMapping.destinationOrigin.model.holder_type
        } else if (model.modelMapping.sourceOrigin instanceof ModelMappingOriginObjectModel && model.modelMapping.destinationOrigin instanceof ModelMappingOriginForm) {
          return objectModelHolderTypeToDocumentOwnerType[model.modelMapping.sourceOrigin.model.holder_type] === model.modelMapping.destinationOrigin.origin.form_owner_type
        } else if (model.modelMapping.destinationOrigin instanceof ModelMappingOriginObjectModel && model.modelMapping.sourceOrigin instanceof ModelMappingOriginForm) {
          return objectModelHolderTypeToDocumentOwnerType[model.modelMapping.destinationOrigin.model.holder_type] === model.modelMapping.sourceOrigin.origin.form_owner_type
        }
      },
      message: "Incompatible Record Holder Type",
      type: ErrorType.incompatibleOwnerTypesError,
    }, this.defaultMappingValueValidatorOptions))
  }

  missingSourceReferenceValidator(): NavigableValidator<ModelMappingValue> {
    return new NavigableValidator(Object.assign({
      validatorKey: 'missingSourceReference',
      section: ValidationModalSection.Layout,
      conditional: (model: ModelMappingValue): boolean => {
        if (ModelMappingCantAddValues.includes(model.modelMapping.rule)) return false
        if (model.source instanceof ModelMappingValueOriginFormElement && model.source.isBuiltin) {
            const source = model.source as ModelMappingValueOriginFormElement
            return source.referencesWorkflowStep
          } else {
            return model.type === ModelMappingValuesType.Value &&
            !!model.source
          }
      },
      validator: (value, model: ModelMappingValue) => {
        return (!!model.source_value_identifier && !!model.source.value)
      },
      message: "Source value must be restored or removed",
      type: ErrorType.referenceNotFound,
    }, this.defaultMappingValueValidatorOptions))
  }

  recordHolderPreserveOwnershipErrorValidator(): NavigableValidator<ModelMappingValue> {
    return new NavigableValidator(Object.assign({
      validatorKey: 'mappingToRecordHolderEmail',
      section: ValidationModalSection.Layout,
      conditional: (model: ModelMappingValue): boolean => {
        if (ModelMappingCantAddValues.includes(model.modelMapping.rule)) return false
        return (
          !!model.source && model.destination_value_identifier === String(ObjectModelBuiltinPropertyType.RecordHolderEmail)
        );
      },
      validator: (value, model: ModelMappingValue) => {
        return !model.modelMapping.enable_preserve_ownership;
      },
      message: "Cannot Map to Record Holder Email and have preserve ownership set to true",
      type: ErrorType.recordHolderPreserveOwnershipError,
    }, this.defaultMappingValueValidatorOptions))
  }

  missingDestinationReferenceValidator(): NavigableValidator<ModelMappingValue> {
    return new NavigableValidator(Object.assign({
      validatorKey: 'missingDestinationReference',
      section: ValidationModalSection.Layout,
      conditional: (model: ModelMappingValue): boolean => {
        if (ModelMappingCantAddValues.includes(model.modelMapping.rule)) return false
        return !!model.destination_value_identifier;
      },
      validator: (value, model: ModelMappingValue) => {
        return (!!model.destination_value_identifier && !!model.destination.value)
      },
      message: "Destination Value must be reset",
      type: ErrorType.referenceNotFound,
    }, this.defaultMappingValueValidatorOptions))
  }

  invalidRecordIdUsageValidator(): NavigableValidator<ModelMappingValue> {
    return new NavigableValidator(Object.assign({
      validatorKey: 'missingSourceReference', // Alex: change this
      section: ValidationModalSection.Layout,
      conditional: (model: ModelMappingValue): boolean => {
        if (ModelMappingCantAddValues.includes(model.modelMapping.rule)) return false
        return (
          model.type === ModelMappingValuesType.Value &&
            model.source.isRecordId
        );
      },
      validator: (value, model: ModelMappingValue) => {
        return (
          // can use record id as lvl 3 source value is rule is "MatchUpdateRelationship
          //   and destination is capable of consuming the record id
          model.modelMapping.rule === ModelMappingRules.MatchUpdateRelationship &&
          model.modelMapping.destinationOrigin.canConsumeRecordId
        ) || (
          // can use record id as lvl 3 source value if mapping from object model to salesforce
          model.modelMapping.isObjectModelToSf || model.modelMapping.isRelationIdentifierToSf
        )
      },
      message: "Source value must be removed",
      type: ErrorType.invalidValue,
    }, this.defaultMappingValueValidatorOptions))
  }

  get defaultMappingValueValidatorOptions() {
    return {
      path: "layout",
      displayName: this.defaultValueDisplayName,
      beforeReveal: async () => {
        await window.requestAnimationFrame(async () => {
          await this.scrollToAndOpenModelMapping();
        })
      },
      containerElementSelector: ".scroll-area.mapping-area",
    }
  }

  defaultValueDisplayName(key, model): string {
    return `${model.source.value?.label} (${model.source.valueType}) -- ${model.directionDisplay} -- ${model.destination.value?.label} (${model.destination.valueType})`
  }

  fileDimensionalTypeMismatchWarningValidator(): NavigableValidator<ModelMappingValue> {
    return new NavigableValidator({
      errorLevel: ValidationErrorLevel.Warning,
      validatorKey: 'fileDimensionalTypeMismatchWarning',
      section: ValidationModalSection.ValueMappings,
      conditional: (model: ModelMappingValue): boolean => {
        if (ModelMappingCantAddValues.includes(model.modelMapping.rule)) return false
        return model.writeOrigin?.isUploader && (model.writeOrigin.fileDimensionalType === DimensionalType.Single);
      },
      validator: (value, model: ModelMappingValue) => {
        return (model.readOrigin.fileDimensionalType === DimensionalType.Single);
      },
      message: "Reading from multiple files while writing to only one file", // this wasn't spec'd, feel free to change
      type: ErrorType.fileDimensionalTypeMismatchWarning,
      path: "layout",
      displayName: (key, model: ModelMappingValue) => {
        // this wasn't spec'd, feel free to change
        return `${model.source.value?.label} (${model.source.valueType}) -- ${model.directionDisplay} -- ${model.destination.value?.label} (${model.destination.valueType})`
      },
      beforeReveal: async () => {
        await window.requestAnimationFrame(async () => {
          await this.scrollToAndOpenModelMapping();
        })
      },
      containerElementSelector: ".scroll-area.mapping-area",
    })
  }

}